<template>
  <div v-if="visible" class="waajo-bulk-afu-backdrop" @click.self="close">
    <div class="waajo-bulk-afu-modal" role="dialog" aria-modal="true" aria-labelledby="waajoBulkAfuTitle">
      <div class="waajo-bulk-afu-head">
        <div>
          <h4 id="waajoBulkAfuTitle"><i class="fa fa-calendar-plus-o"></i> Jadwalkan Auto Follow-up Massal</h4>
          <p>Hanya tiket berstatus Resolved yang akan dijadwalkan. Setiap tiket tetap memiliki recipient dan histori sendiri.</p>
        </div>
        <button type="button" class="waajo-bulk-afu-close" :disabled="submitting || confirming" @click="close">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="waajo-bulk-afu-body">
        <div class="waajo-bulk-afu-summary">
          <div><span>Dipilih</span><strong>{{ ticketSnapshot.length }}</strong></div>
          <div><span>Eligible</span><strong>{{ eligibleTickets.length }}</strong></div>
          <div><span>Dilewati</span><strong>{{ skippedTickets.length }}</strong></div>
        </div>

        <div v-if="!ticketSnapshot.length" class="waajo-bulk-afu-state is-error">
          <i class="fa fa-exclamation-circle"></i>
          <span>Tidak ada tiket yang dipilih.</span>
        </div>

        <div v-else-if="!eligibleTickets.length" class="waajo-bulk-afu-state is-warning">
          <i class="fa fa-info-circle"></i>
          <span>Auto Follow-up hanya tersedia untuk Ticket Resolved. Tidak ada tiket eligible pada pilihan ini.</span>
        </div>

        <template v-else>
          <div v-if="skippedTickets.length" class="waajo-bulk-afu-skip">
            <strong>{{ skippedTickets.length }} tiket dilewati</strong>
            <span>Status bukan Resolved: {{ skippedPreview }}</span>
          </div>

          <div v-if="!hasResults" class="waajo-bulk-afu-field">
            <label>Nama Batch Auto Follow-up</label>
            <input v-model.trim="batchName" type="text" maxlength="255" :disabled="submitting" placeholder="Nama batch">
            <small>Nama batch hanya untuk pengelompokan administrasi, bukan judul pesan.</small>
          </div>

          <template v-if="!hasResults">
            <div class="waajo-bulk-afu-field">
              <label>Pelanggan Eligible</label>
              <div class="waajo-bulk-afu-search">
                <i class="fa fa-search"></i>
                <input v-model.trim="search" type="text" placeholder="Cari nama atau nomor pelanggan..." :disabled="submitting">
              </div>
              <div class="waajo-bulk-afu-ticket-list">
                <div v-for="ticket in filteredEligibleTickets" :key="ticket.id" class="waajo-bulk-afu-ticket">
                  <div class="waajo-bulk-afu-ticket-main">
                    <strong>{{ ticket.contact_name || ticket.contact_number }}</strong>
                    <span>{{ ticket.ticket_number }} · {{ ticket.title }}</span>
                  </div>
                  <div class="waajo-bulk-afu-ticket-meta">
                    <span>{{ formatTicketPhone(ticket.contact_number) }}</span>
                    <span>{{ formatTicketPhone(ticket.license_phone_number, ticket.license_name || '-') }}</span>
                    <span>{{ ticketCategoryLabel(ticket.category) }}</span>
                    <em>{{ ticketStatusLabel(ticket.status) }}</em>
                  </div>
                </div>
                <div v-if="!filteredEligibleTickets.length" class="waajo-bulk-afu-state">
                  Pelanggan tidak ditemukan pada tiket eligible.
                </div>
              </div>
            </div>

            <div class="waajo-bulk-afu-section">
              <div class="waajo-bulk-afu-section-head">
                <h5>Template Auto Follow-up</h5>
                <button type="button" class="waajo-bulk-afu-link" :disabled="templatesLoading || submitting" @click="loadTemplates">
                  <i :class="templatesLoading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i> Muat ulang
                </button>
              </div>
              <div class="waajo-bulk-afu-search">
                <i class="fa fa-search"></i>
                <input v-model.trim="templateSearch" type="text" placeholder="Cari template..." :disabled="submitting" @input="onTemplateSearch">
              </div>
              <div v-if="templatesLoading" class="waajo-bulk-afu-state">
                <i class="fa fa-spinner fa-spin"></i> Memuat template...
              </div>
              <div v-else-if="templatesError" class="waajo-bulk-afu-state is-error">
                <i class="fa fa-exclamation-circle"></i>
                <span>{{ templatesError }}</span>
                <button type="button" @click="loadTemplates">Coba lagi</button>
              </div>
              <div v-else-if="!templates.length" class="waajo-bulk-afu-empty">
                <i class="fa fa-folder-open-o"></i>
                <strong>Belum ada template Auto Follow-up.</strong>
                <p>Buat template dari halaman Auto Follow-up existing, lalu buka kembali modal ini.</p>
                <div>
                  <button type="button" class="waajo-bulk-afu-btn is-primary" @click="goToAfuList">Buat Template</button>
                  <button type="button" class="waajo-bulk-afu-btn" @click="goToAfuTemplates">Kelola Template</button>
                </div>
              </div>
              <div v-else class="waajo-bulk-afu-template-list">
                <button
                  v-for="template in templates"
                  :key="template.id"
                  type="button"
                  class="waajo-bulk-afu-template"
                  :class="{ 'is-selected': selectedTemplate && selectedTemplate.id === template.id }"
                  :disabled="submitting"
                  @click="selectTemplate(template)"
                >
                  <span><strong>{{ template.name }}</strong><small>{{ template.description || 'Tanpa deskripsi' }}</small></span>
                  <i class="fa fa-angle-right"></i>
                </button>
              </div>
            </div>

            <div v-if="selectedTemplate" class="waajo-bulk-afu-preview">
              <div v-if="detailLoading" class="waajo-bulk-afu-state"><i class="fa fa-spinner fa-spin"></i> Memuat detail template...</div>
              <div v-else-if="detailError" class="waajo-bulk-afu-state is-error">
                <i class="fa fa-exclamation-circle"></i>
                <span>{{ detailError }}</span>
                <button type="button" @click="selectTemplate(selectedTemplate)">Coba lagi</button>
              </div>
              <template v-else-if="templateDetail">
                <div class="waajo-bulk-afu-preview-head">
                  <div>
                    <h5>{{ templateDetail.name }}</h5>
                    <p>{{ templateDetail.description || 'Tanpa deskripsi' }}</p>
                  </div>
                  <span>{{ templateSteps.length }} tahap · {{ eligibleTickets.length }} penerima</span>
                </div>
                <div class="waajo-bulk-afu-preview-meta">
                  <div><span>Jendela kirim</span><strong>{{ templateConfig.sending_start_time || '-' }}–{{ templateConfig.sending_end_time || '-' }}</strong></div>
                  <div><span>Berhenti saat dibalas</span><strong>{{ Number(templateConfig.stop_on_customer_reply) === 1 ? 'Ya' : 'Tidak' }}</strong></div>
                  <div><span>Batas harian</span><strong>{{ templateConfig.daily_contact_limit || '-' }} kontak</strong></div>
                </div>
                <div class="waajo-bulk-afu-steps">
                  <div v-for="step in templateSteps" :key="step.step_order" class="waajo-bulk-afu-step">
                    <span class="waajo-bulk-afu-step-no">{{ step.step_order }}</span>
                    <div>
                      <strong>{{ step.name || 'Tahap' }} <small>{{ step.delay_value }} {{ step.delay_unit }}</small></strong>
                      <p>{{ step.message_content || '-' }}</p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>

          <div v-if="hasResults" class="waajo-bulk-afu-results">
            <div class="waajo-bulk-afu-result-head">
              <div>
                <h5>Hasil Penjadwalan</h5>
                <p>{{ batchNameResult || batchName }}</p>
              </div>
              <span>{{ resultCounts.totalScheduled }} berhasil · {{ resultCounts.totalFailed }} gagal · {{ resultCounts.totalSkipped }} dilewati</span>
            </div>
            <div class="waajo-bulk-afu-result-summary">
              <div><span>Total diminta</span><strong>{{ totalRequested }}</strong></div>
              <div><span>Berhasil</span><strong>{{ resultCounts.totalScheduled }}</strong></div>
              <div><span>Gagal</span><strong>{{ resultCounts.totalFailed }}</strong></div>
              <div><span>Dilewati</span><strong>{{ resultCounts.totalSkipped }}</strong></div>
            </div>
            <div class="waajo-bulk-afu-result-list">
              <div v-for="item in results" :key="item.ticket_id" class="waajo-bulk-afu-result-item" :class="'is-' + item.status">
                <div>
                  <strong>{{ item.ticket_number || ('Ticket #' + item.ticket_id) }}</strong>
                  <span>{{ item.contact_name || item.contact_phone || '-' }}</span>
                </div>
                <div class="waajo-bulk-afu-result-status">
                  <em>{{ resultStatusLabel(item.status) }}</em>
                  <small v-if="item.reason">{{ resultReasonLabel(item.reason) }}</small>
                </div>
              </div>
            </div>
            <div v-if="resultCounts.totalFailed" class="waajo-bulk-afu-retry">
              <button type="button" class="waajo-bulk-afu-btn is-primary" :disabled="submitting || confirming" @click="retryFailed">
                <i :class="submitting ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                Retry {{ resultCounts.totalFailed }} tiket gagal
              </button>
              <small>Hanya tiket gagal yang dikirim ulang. Tiket yang sudah berhasil tidak dijadwalkan ulang.</small>
            </div>
          </div>
        </template>

        <div v-if="bulkError" class="waajo-bulk-afu-state is-error">
          <i class="fa fa-exclamation-circle"></i>
          <span>{{ bulkError }}</span>
        </div>
      </div>

      <div class="waajo-bulk-afu-footer">
        <button type="button" class="waajo-bulk-afu-btn" :disabled="submitting || confirming" @click="close">
          {{ hasResults ? 'Tutup' : 'Batal' }}
        </button>
        <button
          v-if="!hasResults && eligibleTickets.length"
          type="button"
          class="waajo-bulk-afu-btn is-primary"
          :disabled="!canSchedule || submitting || confirming"
          @click="scheduleSelected"
        >
          <i v-if="submitting || confirming" class="fa fa-spinner fa-spin"></i>
          {{ submitting || confirming ? 'Menjadwalkan...' : ('Jadwalkan ' + eligibleTickets.length + ' Tiket') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import {
  formatTicketPhone,
  ticketCategoryLabel,
  ticketStatusLabel,
  extractApiErrorMessage
} from '@/utils/ticket'

export default {
  name: 'TicketBulkFollowUpModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    tickets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ticketSnapshot: [],
      search: '',
      batchName: '',
      templateSearch: '',
      templates: [],
      templatesLoading: false,
      templatesError: '',
      templatesRequestId: 0,
      templateSearchTimer: null,
      selectedTemplate: null,
      templateDetail: null,
      detailLoading: false,
      detailError: '',
      detailRequestId: 0,
      submitting: false,
      confirming: false,
      bulkError: '',
      results: [],
      totalRequested: 0,
      batchId: '',
      batchNameResult: '',
      requestKey: ''
    }
  },
  computed: {
    eligibleTickets() {
      return this.ticketSnapshot.filter(ticket => ticket.status === 'resolved')
    },
    skippedTickets() {
      return this.ticketSnapshot.filter(ticket => ticket.status !== 'resolved')
    },
    filteredEligibleTickets() {
      const keyword = this.search.toLowerCase()
      if (!keyword) return this.eligibleTickets
      return this.eligibleTickets.filter(ticket => {
        return String(ticket.contact_name || '').toLowerCase().indexOf(keyword) !== -1 ||
          String(ticket.contact_number || '').toLowerCase().indexOf(keyword) !== -1 ||
          String(ticket.ticket_number || '').toLowerCase().indexOf(keyword) !== -1 ||
          String(ticket.title || '').toLowerCase().indexOf(keyword) !== -1
      })
    },
    skippedPreview() {
      return this.skippedTickets
        .slice(0, 5)
        .map(ticket => ticket.ticket_number + ' (' + ticketStatusLabel(ticket.status) + ')')
        .join(', ') + (this.skippedTickets.length > 5 ? ', ...' : '')
    },
    defaultBatchName() {
      const date = new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
      const categories = []
      this.eligibleTickets.forEach(ticket => {
        if (ticket.category && categories.indexOf(ticket.category) === -1) categories.push(ticket.category)
      })
      if (categories.length === 1) {
        return 'Follow-up ' + ticketCategoryLabel(categories[0]) + ' - ' + date
      }
      return 'Follow-up Penyelesaian Tiket - ' + date
    },
    templateConfig() {
      return this.templateDetail && this.templateDetail.config ? this.templateDetail.config : {}
    },
    templateSteps() {
      return Array.isArray(this.templateConfig.steps) ? this.templateConfig.steps : []
    },
    canSchedule() {
      return this.eligibleTickets.length > 0 &&
        !!String(this.batchName || '').trim() &&
        !!this.selectedTemplate &&
        !!this.selectedTemplate.id &&
        !!this.templateDetail &&
        this.templateSteps.length > 0 &&
        !this.submitting &&
        !this.confirming
    },
    hasResults() {
      return this.results.length > 0
    },
    resultCounts() {
      return this.results.reduce((acc, item) => {
        if (item.status === 'scheduled') acc.totalScheduled++
        else if (item.status === 'failed') acc.totalFailed++
        else acc.totalSkipped++
        return acc
      }, { totalScheduled: 0, totalFailed: 0, totalSkipped: 0 })
    }
  },
  watch: {
    visible(value) {
      if (value) this.initialize()
    }
  },
  beforeDestroy() {
    if (this.templateSearchTimer) clearTimeout(this.templateSearchTimer)
  },
  methods: {
    formatTicketPhone,
    ticketCategoryLabel,
    ticketStatusLabel,
    initialize() {
      this.ticketSnapshot = (this.tickets || []).slice()
      this.search = ''
      this.batchName = this.defaultBatchName
      this.templateSearch = ''
      this.templates = []
      this.templatesError = ''
      this.selectedTemplate = null
      this.templateDetail = null
      this.detailError = ''
      this.bulkError = ''
      this.results = []
      this.totalRequested = 0
      this.batchId = ''
      this.batchNameResult = ''
      this.requestKey = this.newRequestKey()
      this.submitting = false
      this.confirming = false
      this.loadTemplates()
    },
    newRequestKey() {
      return 'bulk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)
    },
    swalAboveModal(options) {
      const base = options || {}
      const customClass = Object.assign({}, base.customClass || {}, {
        container: 'waajo-swal-above-ticket-modal'
      })
      return this.$fire(Object.assign({}, base, {
        customClass,
        customContainerClass: 'waajo-swal-above-ticket-modal'
      }))
    },
    close() {
      if (this.submitting || this.confirming) return
      this.$emit('close')
    },
    goToAfuList() {
      this.$emit('close')
      this.$router.push('/auto-follow-up')
    },
    goToAfuTemplates() {
      this.$emit('close')
      this.$router.push('/auto-follow-up/templates')
    },
    onTemplateSearch() {
      if (this.templateSearchTimer) clearTimeout(this.templateSearchTimer)
      this.templateSearchTimer = setTimeout(() => {
        this.templateSearchTimer = null
        this.loadTemplates()
      }, 320)
    },
    async loadTemplates() {
      const requestId = this.templatesRequestId + 1
      this.templatesRequestId = requestId
      this.templatesLoading = true
      this.templatesError = ''
      try {
        const response = await this.$store.dispatch('autoFollowUp/templateListAutoFollowUp', {
          page: 1,
          per_page: 20,
          search: this.templateSearch
        })
        if (requestId !== this.templatesRequestId) return
        const envelope = response || {}
        const payload = envelope.data !== undefined ? envelope.data : envelope
        const rows = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload.data) ? payload.data : [])
        this.templates = rows
      } catch (error) {
        if (requestId !== this.templatesRequestId) return
        this.templates = []
        this.templatesError = extractApiErrorMessage(error, 'Template Auto Follow-up gagal dimuat.')
      } finally {
        if (requestId === this.templatesRequestId) this.templatesLoading = false
      }
    },
    async selectTemplate(template) {
      const requestId = this.detailRequestId + 1
      this.detailRequestId = requestId
      this.selectedTemplate = template
      this.detailLoading = true
      this.detailError = ''
      try {
        const response = await this.$store.dispatch('autoFollowUp/templateDetailAutoFollowUp', { id: template.id })
        if (requestId !== this.detailRequestId) return
        this.templateDetail = response && response.data ? response.data : null
      } catch (error) {
        if (requestId !== this.detailRequestId) return
        this.templateDetail = null
        this.detailError = extractApiErrorMessage(error, 'Detail template gagal dimuat.')
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
      }
    },
    validateSchedule() {
      if (!this.eligibleTickets.length) return 'Tidak ada Ticket Resolved yang eligible.'
      if (!String(this.batchName || '').trim()) return 'Nama batch wajib diisi.'
      if (!this.selectedTemplate || !String(this.selectedTemplate.id || '').trim()) return 'Template belum dipilih.'
      if (!this.templateDetail) return 'Detail template belum berhasil dimuat.'
      if (!this.templateSteps.length) return 'Template tidak memiliki tahapan pesan.'
      if (this.eligibleTickets.some(ticket => !ticket.id)) return 'Ada tiket eligible tanpa ID yang valid.'
      return ''
    },
    async performBulkSchedule(ticketIds, templateId, batchName, requestKey) {
      if (this.submitting) throw new Error('Penjadwalan sedang diproses.')
      this.submitting = true
      try {
        const response = await this.$store.dispatch('ticket/bulkScheduleFollowUp', {
          batch_name: batchName,
          template_id: templateId,
          ticket_ids: ticketIds,
          idempotency_key: requestKey
        })
        const payload = response && response.data ? response.data : null
        if (!payload || !payload.batch_id || !Array.isArray(payload.results) || !payload.results.length) {
          throw new Error('Backend tidak mengembalikan hasil bulk scheduling yang valid.')
        }
        return payload
      } finally {
        this.submitting = false
      }
    },
    async scheduleSelected() {
      if (this.submitting || this.confirming) return
      const validationError = this.validateSchedule()
      if (validationError) {
        this.bulkError = validationError
        this.swalAboveModal({ title: 'Data Belum Lengkap', text: validationError, type: 'warning' })
        return
      }
      const ticketIds = this.eligibleTickets.map(ticket => Number(ticket.id))
      const templateId = String(this.selectedTemplate.id)
      const batchName = String(this.batchName || '').trim()
      const requestKey = this.requestKey
      let payloadResult = null
      this.bulkError = ''
      this.confirming = true
      try {
        const confirmation = await this.swalAboveModal({
          title: 'Jadwalkan Auto Follow-up Massal?',
          text:
            ticketIds.length + ' tiket Resolved akan dijadwalkan dengan template "' + this.selectedTemplate.name + '". ' +
            (this.skippedTickets.length ? this.skippedTickets.length + ' tiket non-Resolved akan dilewati. ' : '') +
            'Setiap tiket memiliki recipient sendiri.',
          showCancelButton: true,
          showLoaderOnConfirm: true,
          confirmButtonText: 'Ya, jadwalkan',
          cancelButtonText: 'Batal',
          type: 'warning',
          confirmButtonColor: '#f39c12',
          preConfirm: () => {
            this.bulkError = ''
            if (typeof Swal.resetValidationMessage === 'function') Swal.resetValidationMessage()
            return this.performBulkSchedule(ticketIds, templateId, batchName, requestKey)
              .then(payload => {
                payloadResult = payload
                return true
              })
              .catch(error => {
                this.bulkError = extractApiErrorMessage(error, 'Bulk Auto Follow-up gagal dijadwalkan.')
                if (typeof Swal.showValidationMessage === 'function') {
                  Swal.showValidationMessage(this.bulkError)
                }
                return false
              })
          }
        })
        if (!confirmation || confirmation.value !== true) {
          if (this.bulkError) {
            this.swalAboveModal({ title: 'Gagal Menjadwalkan', text: this.bulkError, type: 'error' })
          }
          return
        }
        this.applyResult(payloadResult, true)
      } catch (error) {
        this.bulkError = extractApiErrorMessage(error, 'Bulk Auto Follow-up gagal dijadwalkan.')
        this.swalAboveModal({ title: 'Gagal Menjadwalkan', text: this.bulkError, type: 'error' })
      } finally {
        this.confirming = false
      }
    },
    applyResult(payload, initial) {
      if (!payload) return
      if (initial) {
        this.results = payload.results.slice()
        this.totalRequested = Number(payload.total_requested) || payload.results.length
        this.batchId = payload.batch_id || ''
        this.batchNameResult = payload.batch_name || this.batchName
      } else {
        const merged = {}
        this.results.forEach(item => { merged[item.ticket_id] = item })
        payload.results.forEach(item => { merged[item.ticket_id] = item })
        this.results = Object.keys(merged).map(key => merged[key])
        this.batchNameResult = this.batchNameResult || payload.batch_name || this.batchName
      }
      this.$emit('completed', {
        batch_id: this.batchId,
        batch_name: this.batchNameResult,
        results: this.results
      })
    },
    async retryFailed() {
      if (this.submitting || this.confirming) return
      const failedTickets = this.results.filter(item => item.status === 'failed')
      if (!failedTickets.length) return
      const ticketIds = failedTickets.map(item => Number(item.ticket_id))
      const templateId = this.selectedTemplate ? String(this.selectedTemplate.id) : String(this.results[0] && this.results[0].template_id || '')
      if (!templateId) {
        this.swalAboveModal({ title: 'Gagal Retry', text: 'Template untuk retry tidak tersedia.', type: 'error' })
        return
      }
      const requestKey = this.newRequestKey()
      let payloadResult = null
      this.bulkError = ''
      this.confirming = true
      try {
        const confirmation = await this.swalAboveModal({
          title: 'Retry Tiket Gagal?',
          text: ticketIds.length + ' tiket gagal akan dicoba ulang. Tiket yang sudah berhasil tidak dijadwalkan ulang.',
          showCancelButton: true,
          showLoaderOnConfirm: true,
          confirmButtonText: 'Ya, retry',
          cancelButtonText: 'Batal',
          type: 'warning',
          confirmButtonColor: '#f39c12',
          preConfirm: () => {
            this.bulkError = ''
            if (typeof Swal.resetValidationMessage === 'function') Swal.resetValidationMessage()
            return this.performBulkSchedule(ticketIds, templateId, this.batchNameResult || this.batchName, requestKey)
              .then(payload => { payloadResult = payload; return true })
              .catch(error => {
                this.bulkError = extractApiErrorMessage(error, 'Retry bulk Auto Follow-up gagal.')
                if (typeof Swal.showValidationMessage === 'function') Swal.showValidationMessage(this.bulkError)
                return false
              })
          }
        })
        if (!confirmation || confirmation.value !== true) {
          if (this.bulkError) this.swalAboveModal({ title: 'Gagal Retry', text: this.bulkError, type: 'error' })
          return
        }
        this.applyResult(payloadResult, false)
      } catch (error) {
        this.bulkError = extractApiErrorMessage(error, 'Retry bulk Auto Follow-up gagal.')
        this.swalAboveModal({ title: 'Gagal Retry', text: this.bulkError, type: 'error' })
      } finally {
        this.confirming = false
      }
    },
    resultStatusLabel(value) {
      if (value === 'scheduled') return 'Dijadwalkan'
      if (value === 'failed') return 'Gagal'
      if (value === 'skipped') return 'Dilewati'
      return value || '-'
    },
    resultReasonLabel(value) {
      if (value === 'ticket_not_resolved') return 'Tiket belum Resolved'
      if (value === 'ticket_not_found') return 'Tiket tidak ditemukan'
      return value || ''
    }
  }
}
</script>

<style scoped>
.waajo-bulk-afu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2300;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 30px 16px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.58);
}
.waajo-bulk-afu-modal {
  width: 100%;
  max-width: 860px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.26);
  overflow: hidden;
}
.waajo-bulk-afu-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--waajo-border);
}
.waajo-bulk-afu-head h4 {
  margin: 0 0 4px;
  font-size: 20px;
  color: var(--waajo-text);
}
.waajo-bulk-afu-head h4 i { color: var(--waajo-brand); margin-right: 7px; }
.waajo-bulk-afu-head p { margin: 0; font-size: 12px; line-height: 1.5; color: var(--waajo-text-muted); }
.waajo-bulk-afu-close { border: 0; background: transparent; color: var(--waajo-text-muted); font-size: 18px; }
.waajo-bulk-afu-body {
  padding: 18px 22px 22px;
  max-height: calc(100vh - 170px);
  overflow-y: auto;
}
.waajo-bulk-afu-summary,
.waajo-bulk-afu-result-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.waajo-bulk-afu-result-summary { grid-template-columns: repeat(4, 1fr); }
.waajo-bulk-afu-summary div,
.waajo-bulk-afu-result-summary div {
  padding: 10px 12px;
  border: 1px solid var(--waajo-border);
  border-radius: 9px;
  background: var(--waajo-brand-soft-2);
}
.waajo-bulk-afu-summary span,
.waajo-bulk-afu-result-summary span {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--waajo-text-faint);
  margin-bottom: 3px;
}
.waajo-bulk-afu-summary strong,
.waajo-bulk-afu-result-summary strong { font-size: 18px; color: var(--waajo-text); }
.waajo-bulk-afu-state,
.waajo-bulk-afu-empty {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 14px 4px;
  color: var(--waajo-text-muted);
  font-size: 13px;
  line-height: 1.5;
}
.waajo-bulk-afu-state.is-warning { color: var(--waajo-brand-ink); }
.waajo-bulk-afu-state.is-error { color: #b42318; }
.waajo-bulk-afu-state button { border: 0; background: transparent; color: #b42318; font-size: 12px; font-weight: 700; text-decoration: underline; }
.waajo-bulk-afu-skip {
  padding: 10px 12px;
  margin-bottom: 14px;
  border: 1px solid #fed7aa;
  border-radius: 9px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-bulk-afu-skip strong { display: block; font-size: 13px; }
.waajo-bulk-afu-field { margin-bottom: 15px; }
.waajo-bulk-afu-field > label { display: block; margin-bottom: 6px; font-size: 14px; font-weight: 700; color: var(--waajo-text-body); }
.waajo-bulk-afu-field > input {
  width: 100%;
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 11px;
  font-size: 14px;
  color: var(--waajo-text);
  outline: none;
}
.waajo-bulk-afu-field > small { display: block; margin-top: 5px; font-size: 12px; color: var(--waajo-text-muted); }
.waajo-bulk-afu-search {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 11px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 8px;
}
.waajo-bulk-afu-search:focus-within { border-color: var(--waajo-brand-border-strong); box-shadow: 0 0 0 3px var(--waajo-focus-ring); }
.waajo-bulk-afu-search i { color: var(--waajo-text-faint); }
.waajo-bulk-afu-search input { flex: 1; border: 0; outline: none; min-height: 36px; font-size: 14px; color: var(--waajo-text); }
.waajo-bulk-afu-ticket-list,
.waajo-bulk-afu-template-list,
.waajo-bulk-afu-result-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 230px;
  overflow-y: auto;
}
.waajo-bulk-afu-ticket {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 11px;
  border: 1px solid var(--waajo-border);
  border-radius: 8px;
  background: #fff;
}
.waajo-bulk-afu-ticket-main { min-width: 0; }
.waajo-bulk-afu-ticket-main strong { display: block; font-size: 13px; color: var(--waajo-text); }
.waajo-bulk-afu-ticket-main span { display: block; margin-top: 2px; font-size: 11px; color: var(--waajo-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.waajo-bulk-afu-ticket-meta { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 5px; align-content: flex-start; }
.waajo-bulk-afu-ticket-meta span,
.waajo-bulk-afu-ticket-meta em {
  padding: 3px 6px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475467;
  font-size: 10px;
  font-style: normal;
  white-space: nowrap;
}
.waajo-bulk-afu-ticket-meta em { background: var(--waajo-status-resolved-bg); color: var(--waajo-status-resolved-ink); font-weight: 700; }
.waajo-bulk-afu-section { margin-top: 8px; }
.waajo-bulk-afu-section-head,
.waajo-bulk-afu-result-head,
.waajo-bulk-afu-preview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.waajo-bulk-afu-section-head h5,
.waajo-bulk-afu-result-head h5,
.waajo-bulk-afu-preview-head h5 { margin: 0; font-size: 15px; color: var(--waajo-text); }
.waajo-bulk-afu-result-head p,
.waajo-bulk-afu-preview-head p { margin: 3px 0 0; font-size: 12px; color: var(--waajo-text-muted); }
.waajo-bulk-afu-result-head > span,
.waajo-bulk-afu-preview-head > span { font-size: 11px; color: var(--waajo-text-muted); white-space: nowrap; }
.waajo-bulk-afu-link { border: 0; background: transparent; color: var(--waajo-brand-ink); font-size: 12px; font-weight: 700; }
.waajo-bulk-afu-template {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 9px 11px;
  border: 1px solid var(--waajo-border);
  border-radius: 8px;
  background: #fff;
  text-align: left;
}
.waajo-bulk-afu-template:hover,
.waajo-bulk-afu-template.is-selected { border-color: var(--waajo-brand-border); background: var(--waajo-brand-soft-2); }
.waajo-bulk-afu-template strong { display: block; font-size: 13px; color: var(--waajo-text); }
.waajo-bulk-afu-template small { display: block; margin-top: 2px; font-size: 11px; color: var(--waajo-text-muted); }
.waajo-bulk-afu-template > i { color: var(--waajo-text-faint); }
.waajo-bulk-afu-preview {
  margin-top: 14px;
  padding: 13px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 10px;
  background: var(--waajo-brand-soft-2);
}
.waajo-bulk-afu-preview-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}
.waajo-bulk-afu-preview-meta span { display: block; font-size: 10px; text-transform: uppercase; color: var(--waajo-text-faint); margin-bottom: 2px; }
.waajo-bulk-afu-preview-meta strong { font-size: 12px; color: var(--waajo-text); }
.waajo-bulk-afu-steps { display: flex; flex-direction: column; gap: 8px; }
.waajo-bulk-afu-step { display: flex; gap: 9px; padding-top: 8px; border-top: 1px solid #f0f3f8; }
.waajo-bulk-afu-step-no {
  width: 24px; height: 24px; flex: 0 0 24px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--waajo-brand-soft); color: var(--waajo-brand-ink); font-size: 11px; font-weight: 700;
}
.waajo-bulk-afu-step strong { display: block; font-size: 12px; color: var(--waajo-text); }
.waajo-bulk-afu-step strong small { margin-left: 5px; font-size: 10px; font-weight: 400; color: var(--waajo-text-faint); }
.waajo-bulk-afu-step p { margin: 3px 0 0; font-size: 12px; line-height: 1.5; color: var(--waajo-text-body); white-space: pre-wrap; }
.waajo-bulk-afu-empty { flex-direction: column; align-items: center; text-align: center; }
.waajo-bulk-afu-empty > i { font-size: 28px; color: #cbd5e1; }
.waajo-bulk-afu-empty strong { color: var(--waajo-text); font-size: 14px; }
.waajo-bulk-afu-empty p { margin: 0; font-size: 12px; }
.waajo-bulk-afu-empty > div { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; margin-top: 5px; }
.waajo-bulk-afu-results { margin-top: 4px; }
.waajo-bulk-afu-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 11px;
  border: 1px solid var(--waajo-border);
  border-radius: 8px;
  background: #fff;
}
.waajo-bulk-afu-result-item.is-failed { border-color: #fecaca; background: #fef2f2; }
.waajo-bulk-afu-result-item.is-skipped { background: #fffbeb; border-color: #fde68a; }
.waajo-bulk-afu-result-item strong { display: block; font-size: 13px; color: var(--waajo-text); }
.waajo-bulk-afu-result-item span { display: block; margin-top: 2px; font-size: 11px; color: var(--waajo-text-muted); }
.waajo-bulk-afu-result-status { text-align: right; }
.waajo-bulk-afu-result-status em { display: inline-block; padding: 3px 7px; border-radius: 999px; background: #f1f5f9; color: #475467; font-size: 10px; font-style: normal; font-weight: 700; }
.waajo-bulk-afu-result-item.is-scheduled .waajo-bulk-afu-result-status em { background: var(--waajo-status-resolved-bg); color: var(--waajo-status-resolved-ink); }
.waajo-bulk-afu-result-item.is-failed .waajo-bulk-afu-result-status em { background: #fee2e2; color: #b91c1c; }
.waajo-bulk-afu-result-item.is-skipped .waajo-bulk-afu-result-status em { background: #fef3c7; color: #92400e; }
.waajo-bulk-afu-result-status small { display: block; margin-top: 3px; font-size: 10px; color: var(--waajo-text-muted); }
.waajo-bulk-afu-retry { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }
.waajo-bulk-afu-retry small { font-size: 11px; color: var(--waajo-text-muted); }
.waajo-bulk-afu-footer {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding: 14px 22px 18px;
  border-top: 1px solid var(--waajo-border);
}
.waajo-bulk-afu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 15px;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  background: #fff;
  color: var(--waajo-text-body);
  font-size: 14px;
  font-weight: 600;
}
.waajo-bulk-afu-btn.is-primary { border-color: var(--waajo-brand); background: var(--waajo-brand); color: #fff; font-weight: 700; }
.waajo-bulk-afu-btn:disabled { opacity: .6; }
@media (max-width: 640px) {
  .waajo-bulk-afu-summary,
  .waajo-bulk-afu-result-summary,
  .waajo-bulk-afu-preview-meta { grid-template-columns: 1fr 1fr; }
  .waajo-bulk-afu-ticket { flex-direction: column; }
  .waajo-bulk-afu-ticket-meta { justify-content: flex-start; }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketBulkFollowUpModal.vue